import React, { useState, useEffect } from 'react';
import settingsService, { StoreConfiguration } from '../services/settingsService';
import { roleService } from '../services/roleService';
import { Role } from '../types/types';
import { Settings as SettingsIcon, Bell, Shield, Receipt, Save } from 'lucide-react';
import './Settings.css';

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<StoreConfiguration | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [activeTab, setActiveTab] = useState<'general' | 'stock' | 'roles' | 'receipt'>('general');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [settingsData, rolesData] = await Promise.all([
        settingsService.getSettings(),
        roleService.getAllRoles()
      ]);
      setSettings(settingsData);
      setRoles(rolesData);
    } catch (error) {
      console.error('Error loading settings:', error);
      setMessage({ type: 'error', text: 'Error al cargar la configuración' });
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!settings) return;
    const { name, value, type } = e.target;
    setSettings({
      ...settings,
      [name]: type === 'number' ? Number.parseFloat(value) || 0 : value
    });
  };

  const handleSaveSettings = async () => {
    if (!settings) return;
    try {
      await settingsService.updateSettings(settings);
      setMessage({ type: 'success', text: 'Configuración guardada correctamente' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Error al guardar la configuración' });
    }
  };

  const togglePermission = async (role: Role, permissionKey: string) => {
    const currentPerms = role.permissions || [];
    let newPerms: string[];

    if (currentPerms.includes(permissionKey)) {
      newPerms = currentPerms.filter((p: string) => p !== permissionKey);
    } else {
      newPerms = [...currentPerms, permissionKey];
    }

    const updatedRole: Role = { ...role, permissions: newPerms };

    try {
      await roleService.updateRole(role.id, updatedRole);
      setRoles(roles.map(r => r.id === role.id ? updatedRole : r));
    } catch (error: any) {
      console.error('Full error updating permissions:', error);
      const errorMsg = error.response?.data?.title || error.response?.data || error.message || 'Error al actualizar permisos';
      setMessage({ type: 'error', text: `Error: ${errorMsg}` });
    }
  };

  const permissionsList = [
    { key: 'products.read', label: 'Ver Productos' },
    { key: 'products.create', label: 'Crear Productos' },
    { key: 'products.update', label: 'Editar Productos' },
    { key: 'products.delete', label: 'Eliminar Productos' },
    { key: 'sales.read', label: 'Ver Ventas' },
    { key: 'sales.create', label: 'Realizar Ventas' },
    { key: 'customers.read', label: 'Ver Clientes' },
    { key: 'customers.create', label: 'Gestionar Clientes' },
    { key: 'settings.update', label: 'Modificar Configuración' },
  ];

  if (loading) return <div className="loading-state">Cargando configuración...</div>;

  return (
    <div className="settings-container">
        <div className="page-header">
          <div>
            <h1>Configuración del Sistema</h1>
            <p className="page-subtitle">Personaliza el funcionamiento de tu POS</p>
          </div>
        </div>

        {message && (
          <div className={`${message.type}-message`}>
            {message.text}
          </div>
        )}

        <div className="settings-tabs">
          <button
            className={`tab-btn ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            <SettingsIcon size={18} /> General
          </button>
          <button
            className={`tab-btn ${activeTab === 'stock' ? 'active' : ''}`}
            onClick={() => setActiveTab('stock')}
          >
            <Bell size={18} /> Alertas de Stock
          </button>
          <button
            className={`tab-btn ${activeTab === 'roles' ? 'active' : ''}`}
            onClick={() => setActiveTab('roles')}
          >
            <Shield size={18} /> Permisos y Roles
          </button>
          <button
            className={`tab-btn ${activeTab === 'receipt' ? 'active' : ''}`}
            onClick={() => setActiveTab('receipt')}
          >
            <Receipt size={18} /> Recibos
          </button>
        </div>

        <div className="settings-card">
          {activeTab === 'general' && settings && (
            <div className="settings-section">
              <h3>Información de la Tienda</h3>
              <div className="form-group">
                <label htmlFor="storeName">Nombre de la Tienda</label>
                <input
                  type="text"
                  id="storeName"
                  name="storeName"
                  value={settings.storeName}
                  onChange={handleSettingsChange}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="address">Dirección</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={settings.address || ''}
                  onChange={handleSettingsChange}
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Teléfono</label>
                  <input
                    type="text"
                    id="phone"
                    name="phone"
                    value={settings.phone || ''}
                    onChange={handleSettingsChange}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={settings.email || ''}
                    onChange={handleSettingsChange}
                    className="form-input"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="currencySymbol">Símbolo de Moneda</label>
                  <input
                    type="text"
                    id="currencySymbol"
                    name="currencySymbol"
                    value={settings.currencySymbol}
                    onChange={handleSettingsChange}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="taxRate">Tasa de Impuesto (%)</label>
                  <input
                    type="number"
                    id="taxRate"
                    name="taxRate"
                    value={settings.taxRate}
                    onChange={handleSettingsChange}
                    className="form-input"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'stock' && settings && (
            <div className="settings-section">
              <h3>Alertas de Inventario</h3>
              <p className="section-desc">Define el umbral global para considerar un producto con stock bajo.</p>
              <div className="form-group">
                <label htmlFor="defaultMinStockThreshold">Umbral de Stock Bajo (Global)</label>
                <input
                  type="number"
                  id="defaultMinStockThreshold"
                  name="defaultMinStockThreshold"
                  value={settings.defaultMinStockThreshold}
                  onChange={handleSettingsChange}
                  className="form-input"
                />
              </div>
            </div>
          )}

          {activeTab === 'roles' && (
            <div className="settings-section">
              <h3>Gestión de Accesos</h3>
              <div className="permissions-grid">
                {roles.map(role => (
                  <div key={role.id} className="role-column">
                    <h4>{role.name}</h4>
                    {permissionsList.map(perm => {
                      const isChecked = role.name === 'Admin' || (role.permissions?.includes(perm.key) ?? false);
                      return (
                        <label key={perm.key} className="permission-item">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={role.name === 'Admin'}
                            onChange={() => togglePermission(role, perm.key)}
                          />
                          <span>{perm.label}</span>
                        </label>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'receipt' && settings && (
            <div className="settings-section">
              <h3>Personalización de Recibos</h3>
              <div className="form-group">
                <label htmlFor="receiptHeader">Encabezado del Recibo</label>
                <textarea
                  id="receiptHeader"
                  name="receiptHeader"
                  value={settings.receiptHeader || ''}
                  onChange={handleSettingsChange}
                  className="form-input"
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label htmlFor="receiptFooter">Pie del Recibo</label>
                <textarea
                  id="receiptFooter"
                  name="receiptFooter"
                  value={settings.receiptFooter || ''}
                  onChange={handleSettingsChange}
                  className="form-input"
                  rows={3}
                />
              </div>
            </div>
          )}

          {activeTab !== 'roles' && (
            <div className="save-bar">
              <button className="btn btn-primary" onClick={handleSaveSettings}>
                <Save size={20} /> Guardar Cambios
              </button>
            </div>
          )}
        </div>
      </div>
  );
};

export default Settings;
